import type { SupportAttachment } from '@travel-planner/shared/schemas';
import { FileText, ImageOff, Paperclip, Video } from 'lucide-react';
import { useState } from 'react';
import { ImageLightbox, Text } from '@/shared/ui';
import css from './ticket-attachments.module.scss';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)$/i;
const VIDEO_EXT = /\.(mp4|mov|webm|avi|mkv|m4v)$/i;

// An attachment is an image (→ thumbnail grid + slider) when its stored
// content type says so, else by filename extension. Everything else (video,
// PDF, unknown) stays a separate file card and never enters the slider.
const isImageAttachment = (a: SupportAttachment): boolean =>
  a.contentType?.startsWith('image/') ?? IMAGE_EXT.test(a.name);

const NonImageIcon = ({ a }: { a: SupportAttachment }) => {
  const isVideo = a.contentType?.startsWith('video/') ?? VIDEO_EXT.test(a.name);
  if (isVideo) return <Video size={16} />;
  if (a.contentType === 'application/pdf' || /\.pdf$/i.test(a.name)) {
    return <FileText size={16} />;
  }
  return <Paperclip size={16} />;
};

type Props = { attachments: SupportAttachment[]; title?: string };

// Read-only attachment gallery shared by the reporter's own-ticket row and the
// management drawer, so both sides see a ticket's files the same way.
export const TicketAttachments = ({ attachments, title }: Props) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, boolean>>({});

  if (attachments.length === 0) return null;
  const imageAtts = attachments.filter(isImageAttachment);
  const otherAtts = attachments.filter((a) => !isImageAttachment(a));

  return (
    <div className={css.root}>
      <Text size="sm" weight="medium">
        Attachments
      </Text>
      {imageAtts.length > 0 && (
        <div className={css.thumbGrid}>
          {imageAtts.map((a, i) => (
            <button
              className={css.thumb}
              key={a.key}
              onClick={() => setLightboxIndex(i)}
              type="button"
            >
              {brokenThumbs[a.key] ? (
                <span className={css.thumbBroken}>
                  <ImageOff size={18} />
                </span>
              ) : (
                <img
                  alt={a.name}
                  className={css.thumbImg}
                  loading="lazy"
                  onError={() =>
                    setBrokenThumbs((b) => ({ ...b, [a.key]: true }))
                  }
                  src={a.url}
                />
              )}
            </button>
          ))}
        </div>
      )}
      {otherAtts.length > 0 && (
        <div className={css.fileList}>
          {otherAtts.map((a) => (
            <a
              className={css.fileCard}
              href={a.url}
              key={a.key}
              rel="noreferrer"
              target="_blank"
            >
              <NonImageIcon a={a} />
              <span className={css.fileName}>{a.name}</span>
            </a>
          ))}
        </div>
      )}
      <ImageLightbox
        images={imageAtts.map((a) => a.url)}
        onClose={() => setLightboxIndex(null)}
        startIndex={lightboxIndex}
        title={title}
      />
    </div>
  );
};
